import PropTypes from 'prop-types';
import { scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { Circle } from '@visx/shape';
import { RadialGradient } from '@visx/gradient';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { withTheme } from 'styled-components';
import { localPoint } from '@visx/event';
import { StyledDotTooltip, StyledDotsChartWrapper } from './DotsChart.styles';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';
import { useWindowSize } from '../../utils';
import uniqid from 'uniqid';

const DotsChart = ({
  data = [],
  padding = 40,
  radius,
  showTooltips,
  emptyDataMessage
}) => {
  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const windowSize = useWindowSize();

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [tooltipTop, setTooltipTop] = useState(0);
  const [tooltipLeft, setTooltipLeft] = useState(0);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );

  let points = [];
  data.map((segment) => {
    points = points.concat(segment.values);
  });
  const [xValues, yValues] = points.reduce(
    ([xList, yList], { x, y }) => {
      xList.push(x);
      yList.push(y);
      return [xList, yList];
    },
    [[], []]
  );

  const minMaxValues = {
    xMin: Math.min(...xValues),
    xMax: Math.max(...xValues),
    yMin: Math.min(...yValues),
    yMax: Math.max(...yValues)
  };
  const xScale = useMemo(
    () =>
      scaleLinear({
        domain: [minMaxValues.xMin, minMaxValues.xMax],
        range: [padding, width - padding],
        clamp: true
      }),
    [width, padding]
  );
  const yScale = useMemo(
    () =>
      scaleLinear({
        domain: [minMaxValues.yMin, minMaxValues.yMax],
        range: [height - padding, padding],
        clamp: true
      }),
    [height, padding]
  );

  const getColor = useCallback((color, index) => {
    const availableColors = [
      '#007ADD',
      '#D6519E',
      '#269F49',
      '#DD2A41',
      '#FAB239',
      '#843CAD',
      '#CA4B21',
      '#0A4170'
    ];
    return color || availableColors[index % availableColors.length];
  }, []);

  const handleMouseMove = useCallback(
    (event, label) => {
      if (!svgRef.current) return;

      const point = localPoint(svgRef.current, event);
      if (!point) return;
      setTooltipLeft(point.x);
      setTooltipTop(point.y);
      setTooltipOpen(true);
      setTooltipData(label);
    },
    [svgRef, xScale, yScale]
  );

  const handleMouseLeave = useCallback((event) => {
    window.setTimeout(() => {
      setTooltipOpen(false);
    }, 300);
  }, []);

  if (!data || data.length === 0) {
    return (
      <StyledDotsChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledDotsChartWrapper>
    );
  }

  return (
    <StyledDotsChartWrapper ref={chartRef}>
      <svg width={width} height={height} ref={svgRef}>
        <rect width={width} height={height} rx={14} fill='white' />
        {data.map((group, index) => (
          <Group key={`group-${chartId.current}-${index}`}>
            <RadialGradient
              to='#ffffff'
              from={getColor(group.color, index)}
              id={`dots-gradient-${chartId.current}-${index}`}
              r='70%'
            />
            {group.values.map((point, i) => (
              <Circle
                key={`point-${chartId.current}-${index}-${i}`}
                id={`point-${chartId.current}-${index}-${i}`}
                className='dot'
                cx={xScale(point.x)}
                cy={yScale(point.y)}
                r={radius}
                fill={`url(#dots-gradient-${chartId.current}-${index})`}
                onMouseMove={(event) => handleMouseMove(event, group.label)}
                onMouseLeave={(event) => handleMouseLeave(event)}
              />
            ))}
          </Group>
        ))}
      </svg>
      {showTooltips &&
        tooltipOpen &&
        tooltipLeft != null &&
        tooltipTop != null && (
          <StyledDotTooltip left={tooltipLeft} top={tooltipTop}>
            <div>{tooltipData}</div>
          </StyledDotTooltip>
        )}
    </StyledDotsChartWrapper>
  );
};

DotsChart.propTypes = {
  data: PropTypes.array,
  padding: PropTypes.number,
  radius: PropTypes.number,
  showTooltips: PropTypes.bool,
  emptyDataMessage: PropTypes.string
};

export default memo(withTheme(DotsChart));
